/**
 * Memory API Client
 *
 * Handles all P3 memory API calls:
 * - Hybrid Vector Memory Tiering
 * - Temporal Context Orchestration
 * - Passive Correction Harvesting
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

export type { TimComboAPIResponse };

// =============================================================================
// HYBRID VECTOR MEMORY TIERING
// =============================================================================

export interface MemoryEntry {
  Entry_ID: string;
  Agent_Name: string;
  Content: string;
  Memory_Type: string;
  Tier: 'hot' | 'warm' | 'cool' | 'cold';
  Access_Count: number;
  Relevance_Score: number;
  Created_At: string;
  Last_Accessed_At: string;
}

export interface MemoryStats {
  hot_count: number;
  warm_count: number;
  cool_count: number;
  cold_count: number;
  type_counts: Record<string, number>;
  avg_access_count: number;
  total_consolidations: number;
}

export interface ConsolidationResult {
  Entries_Processed: number;
  Entries_Promoted: number;
  Entries_Demoted: number;
  Patterns_Extracted: number;
}

export async function searchMemory(
  clientId: string,
  query: string,
  opts: { Tier?: string; Memory_Type?: string; limit?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.SEARCH,
    'POST',
    { Client_ID: clientId, query, ...opts }
  );
}

export async function getMemoryEntries(
  clientId: string,
  opts: { Tier?: string; Memory_Type?: string; limit?: number; offset?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.ENTRIES,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

export async function consolidateMemory(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.CONSOLIDATE,
    'POST',
    { Client_ID: clientId }
  );
}

export async function getMemoryStats(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.STATS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

// =============================================================================
// TEMPORAL CONTEXT ORCHESTRATION
// =============================================================================

export interface TemporalPattern {
  Pattern_ID: string;
  Pattern_Type: string;
  Agent_Name: string;
  Trigger_Conditions: Record<string, unknown>;
  Confidence: number;
  Activation_Count: number;
  Success_Count: number;
}

export interface TemporalPrediction {
  Prediction_ID: string;
  Agent_Name: string;
  Predicted_Action: string;
  Confidence: number;
  Was_Correct: boolean | null;
}

export async function getTemporalContext(
  clientId: string,
  opts: { Agent_Name?: string; limit?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.TEMPORAL_CONTEXT,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

export async function getTemporalPatterns(
  clientId: string,
  opts: { Agent_Name?: string; Pattern_Type?: string; min_confidence?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.TEMPORAL_PATTERNS,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

export async function getTemporalPredictions(
  clientId: string,
  opts: { Agent_Name?: string; limit?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.TEMPORAL_PREDICTIONS,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

// =============================================================================
// PASSIVE CORRECTION HARVESTING
// =============================================================================

export interface HarvestedCorrection {
  Harvest_ID: string;
  Source_Type: string;
  Agent_Name: string;
  Extracted_Rule: string;
  Domain: string;
  Confidence: number;
  Signal_Strength: 'strong' | 'moderate' | 'weak';
}

export async function runCorrectionHarvest(
  clientId: string,
  data: { Source_Types?: string[]; Agent_Names?: string[] } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.CORRECTION_HARVEST_RUN,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getHarvestedCorrections(
  clientId: string,
  opts: { Agent_Name?: string; Source_Type?: string; limit?: number; offset?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.CORRECTIONS_GET,
    'GET',
    null,
    { params: { Client_ID: clientId, ...opts } }
  );
}

export async function getCorrectionHarvestStats(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.MEMORY.CORRECTION_HARVEST_STATS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}
